import {
    Box, Paper, Table, TableHead, TableRow, TableCell, TableBody,
    TableSortLabel, Typography, Chip, Stack, Button, CircularProgress,
} from '@mui/material';
import { formatPrice, formatUnitPrice, formatNumber } from '../../utils/format';
import { pieceUnitPrice, lineTotal } from '../../utils/pricing';
import { b2bColors } from '../../styles/b2bTheme';
import QuantityStepper from '../../components/QuantityStepper';
import ProductThumb from '../../components/ProductThumb';
import FavoriteButton from '../../components/FavoriteButton';

const COLUMNS = [
    { key: 'favorite', label: '',          align: 'center', sortable: false, width: 48 },
    { key: 'image',    label: '',          align: 'left',   sortable: false, width: 60 },
    { key: 'sku',      label: 'SKU',       align: 'left',   sortable: true  },
    { key: 'name',     label: 'Produit',   align: 'left',   sortable: true  },
    { key: 'category', label: 'Catégorie', align: 'left',   sortable: true  },
    { key: 'price',    label: 'Prix / caisse', align: 'right', sortable: true, className: 'b2b-num' },
    { key: 'piece',    label: 'Prix unitaire', align: 'right', sortable: false, className: 'b2b-num' },
    { key: 'stock',    label: 'Stock',     align: 'right',  sortable: true,  className: 'b2b-num' },
    { key: 'pack',     label: 'Pack',      align: 'center', sortable: false },
    { key: 'add',      label: 'Ajouter',   align: 'right',  sortable: false, minWidth: 340 },
];

export default function CatalogueTable({
    products, loading, sortBy, sortOrder, onSortClick,
    quantityFor, onQuantityChange, onAdd,
}) {
    // "loading" when we already have data means "refetching" — we keep stale rows
    // visible with a dimmed overlay instead of flashing skeletons (critical for
    // high-RTT networks — a sort click shouldn't feel like a page reload).
    const showStaleOverlay = loading && products && products.length > 0;

    return (
        <Paper sx={{ overflow: 'hidden', position: 'relative' }}>
            <Box sx={{ opacity: showStaleOverlay ? 0.55 : 1, transition: 'opacity 120ms' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {COLUMNS.map((col) => (
                                <TableCell
                                    key={col.key}
                                    align={col.align}
                                    className={col.className}
                                    sortDirection={sortBy === col.key ? sortOrder : false}
                                    sx={{
                                        ...(col.minWidth ? { minWidth: col.minWidth } : {}),
                                        ...(col.width ? { width: col.width } : {}),
                                    }}
                                >
                                    {col.sortable ? (
                                        <TableSortLabel
                                            active={sortBy === col.key}
                                            direction={sortBy === col.key ? sortOrder : 'asc'}
                                            onClick={() => onSortClick(col.key)}
                                        >
                                            {col.label}
                                        </TableSortLabel>
                                    ) : col.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {products.map((p) => (
                            <TableRow key={p.id} hover>
                                <TableCell align="center" sx={{ py: 0.5, px: 0.5 }}>
                                    <FavoriteButton product={p} />
                                </TableCell>
                                <TableCell sx={{ py: 0.5 }}>
                                    <ProductThumb src={p.image_url} alt={p.name} />
                                </TableCell>
                                <TableCell className="b2b-num" sx={{ color: b2bColors.textMuted, fontSize: 12 }}>
                                    {p.sku}
                                </TableCell>
                                <TableCell>{p.name}</TableCell>
                                <TableCell sx={{ color: b2bColors.textMuted, fontSize: 13 }}>
                                    {p.category?.name || '—'}
                                </TableCell>
                                <TableCell align="right" className="b2b-num" sx={{ fontWeight: 600 }}>
                                    {formatPrice(p.unit_price)}
                                </TableCell>
                                <TableCell align="right" className="b2b-num" sx={{ color: b2bColors.textMuted }}>
                                    {formatUnitPrice(pieceUnitPrice(p))}
                                    {p.case_pack > 1 && (
                                        <Typography component="span" variant="body2" sx={{ display: 'block', fontSize: 10, color: b2bColors.textDisabled }}>
                                            la pièce
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell align="right" className="b2b-num">
                                    {p.total_on_hand > 0 ? formatNumber(p.total_on_hand) : (
                                        <Typography component="span" variant="body2" sx={{ color: b2bColors.warning }}>
                                            Rupture
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell align="center" className="b2b-num">
                                    {p.case_pack > 1
                                        ? <Chip size="small" variant="outlined" label={`${p.case_pack} / caisse`} />
                                        : '—'}
                                </TableCell>
                                <TableCell align="right">
                                    <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                                        <Box sx={{ textAlign: 'right', minWidth: 76 }}>
                                            <Typography className="b2b-num" sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.2 }}>
                                                {formatPrice(lineTotal(p, quantityFor(p)))}
                                            </Typography>
                                            <Typography sx={{ fontSize: 10, color: b2bColors.textDisabled, lineHeight: 1.2 }}>
                                                total
                                            </Typography>
                                        </Box>
                                        <QuantityStepper
                                            value={quantityFor(p)}
                                            onChange={(v) => onQuantityChange(p.id, v)}
                                            casePack={p.case_pack}
                                            ariaLabel={`Quantité pour ${p.name}`}
                                        />
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={() => onAdd(p)}
                                            disabled={quantityFor(p) <= 0 || p.total_on_hand <= 0}
                                        >
                                            Ajouter
                                        </Button>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>

            {showStaleOverlay && (
                <Box sx={{
                    position: 'absolute', top: 8, right: 12,
                    display: 'flex', alignItems: 'center', gap: 1,
                    color: b2bColors.textMuted, fontSize: 12,
                }}>
                    <CircularProgress size={14} thickness={5} />
                    Mise à jour…
                </Box>
            )}
        </Paper>
    );
}
